import React, { PureComponent } from 'react';
import { withRouter } from 'react-router-dom';
import isEqual from 'lodash.isequal';
import classnames from 'classnames';
import * as PropTypes from 'prop-types';
import { lock, clearBodyLocks } from 'tua-body-scroll-lock';
import { withTheme } from '@ifit/themes/withTheme';
import { ThemeContext } from '@ifit/themes/ThemeContext';
import { isMobile, isSevenInchTablet } from '../../../../helpers/responsive';
import { bindFilterOptionsState } from '../../../../helpers/filter-options';
import { addFilter } from '../../../../helpers/quickFiltersStore';
import getTransitionEndEventName from '../../../../helpers/get-transition-end-event-name';
import {
  applyButtonText,
  clearButtonText,
} from '../../../../i18n/translations';
import getFilterData from '../get-filter-data';
import FilterDropdown from '../filter-dropdown';
import { SearchField } from '../search-field';
import '../index.scss';
import { getSearchState, SearchState } from '../../state/search-state';
import { parseQueryString } from '../../state/query-string';
import { useGetFilterOptions } from '../../../../client/query/filter-options';
import { withHooks } from '../../../../components/with-query';
import { ensureOnSearchPage, setSearchPath } from '../../state/on-search-page';
import { isSmallSearchDevice } from '../is-small-search';
import { history } from '../../../../store';
import { useDeviceType } from '../../../../store/device-type';
import { useConfig } from '../../../../store/reducers/config';

export class SearchHeader extends PureComponent {
  static contextType = ThemeContext;

  state = {
    currentFilters: null,
    appliedFilters: null,
    filterData: getFilterData(),
    filterOptionsSet: false,
    filtersClearCount: 0,
    filtersOpen: this.defaultMenuOpen,
    menuHeight: null,
    sortByOpen: null,
    isScrollLocked: null,
  };

  targetElement = null;

  constructor(props) {
    super(props);

    bindFilterOptionsState(() => this.state.filtersOpen, this.menuStateChanged);
    this.ensureFiltersVisible = this.ensureFiltersVisible.bind(this);
  }

  componentDidMount() {
    getSearchState().onStateChanged(this.searchStateChanged);
    this.calculateHeight();
    this.setTargetElement();
  }

  componentDidUpdate(...args) {
    this.calculateHeight();
    this.setTargetElement();
    return ['checkForFilterDataChange', 'checkForMenuChange'].map(method =>
      this[method](...args),
    );
  }

  get defaultMenuOpen() {
    return Boolean(parseQueryString().showFilterOptions);
  }

  calculateHeight() {
    const height = isSmallSearchDevice(this.props.deviceType)
      ? this.mobileTextContain?.clientHeight
      : this.searchMenu?.clientHeight;
    if (this.state.menuHeight !== height) {
      this.setState({
        menuHeight: height,
      });
    }
  }

  menuStateChanged = filtersOpen => {
    this.setState({ filtersOpen });
    if (!filtersOpen) {
      const textSearchField = this.searchField?.textInput;
      if (textSearchField) {
        textSearchField.blur();
      }
    }
  };

  searchStateChanged = (params, appliedChanged) => {
    this.setState(params, () => {
      if (appliedChanged) {
        this.setState({ filtersOpen: false });
        this.updateQuickFiltersStore();
      }
    });
  };

  checkForFilterDataChange = prevProps => {
    const [
      { filterOptions: oldFilterOptions, language: oldLocale },
      { filterOptions: newFilterOptions, language: newLocale },
    ] = [prevProps, this.props];
    const { filterOptionsSet } = this.state;
    const needsFilterDataUpdate = Boolean(
      (newFilterOptions && !filterOptionsSet) ||
        !isEqual(oldFilterOptions, newFilterOptions) ||
        oldLocale !== newLocale,
    );
    if (needsFilterDataUpdate) {
      this.setState({
        filterOptionsSet: true,
        filterData: getFilterData(newFilterOptions),
      });
    }
  };

  focusSearchField = () => this.searchField?.textInput.focus();

  checkForMenuChange = (_, prevState) => {
    const [
      { filtersOpen: previousFiltersOpen },
      { filtersOpen: currentFiltersOpen },
    ] = [prevState, this.state];
    if (
      previousFiltersOpen === currentFiltersOpen &&
      this.state.isScrollLocked !== null &&
      prevState.sortByOpen === this.state.sortByOpen
    ) {
      return;
    }

    const action =
      (currentFiltersOpen && isMobile(this.props.deviceType)) ||
      (this.state.sortByOpen && isMobile(this.props.deviceType)) ||
      (this.state.filtersOpen && !isMobile(this.props.deviceType))
        ? this.lock
        : this.clearBodyLocks;
    return action(this.targetElement);
  };

  componentWillUnmount() {
    clearBodyLocks();
    getSearchState().offStateChanged(this.searchStateChanged);

    if (this.searchMenu) {
      this.searchMenu.removeEventListener(
        getTransitionEndEventName(),
        this.focusSearchField,
      );
    }
  }

  setTargetElement = () => {
    const previousTargetElement = this.targetElement;

    if (this.state.filtersOpen) {
      this.targetElement = document.querySelector('.contain-filter');
      if (previousTargetElement !== this.targetElement) {
        this.clearBodyLocks();
      }
    } else if (this.state.sortByOpen) {
      this.targetElement = document.querySelector('.radio-option-container');
      if (previousTargetElement !== this.targetElement) {
        this.clearBodyLocks();
      }
    }
  };

  lock = element => {
    lock(element);
    this.setState({
      isScrollLocked: true,
    });
  };

  clearBodyLocks = () => {
    clearBodyLocks();
    this.setState({
      isScrollLocked: false,
    });
  };

  isSortDropdownOpen = isSortByOpen => {
    if (this.state.sortByOpen !== isSortByOpen)
      this.setState({ sortByOpen: isSortByOpen });
  };

  onDropdownSelect = dropdownElement => {
    const containFilterElement = document.querySelector('.contain-filter');

    if (containFilterElement && dropdownElement) {
      setTimeout(() => {
        const containerRect = containFilterElement.getBoundingClientRect();
        const dropdownRect = dropdownElement.getBoundingClientRect();
        const scrollTop =
          containFilterElement.scrollTop + dropdownRect.top - containerRect.top;
        containFilterElement.scrollTo({
          top: scrollTop,
          behavior: 'smooth',
        });
      }, 100);
    }
  };

  onFilterCheck = (filter, value, shouldApply) => {
    getSearchState().changeFilter(filter, value, shouldApply);
  };

  onClearFilters = () => {
    getSearchState().applyDefaultFilters();
    this.setState(({ filtersClearCount }) => ({
      filtersClearCount: filtersClearCount + 1,
    }));
    getSearchState().applyCurrentFilters();
  };

  applyFilters = () => {
    getSearchState().resetPreviousFilters();
    getSearchState().applyCurrentFilters();
  };

  updateQuickFiltersStore = () => {
    const { appliedFilters, filterData } = this.state;

    for (const appliedFilter in appliedFilters) {
      const { options = [], singleValueFormatter } =
        filterData.find(filter => filter.dataLabel === appliedFilter) || {};

      const selectedOptions = options.filter(
        option =>
          appliedFilters[appliedFilter]?.includes(option.value) &&
          !SearchState.defaultFilters[appliedFilter]?.includes(option.value),
      );

      selectedOptions.forEach(option => {
        addFilter({
          type: appliedFilter,
          filter: option.value,
          display: singleValueFormatter
            ? singleValueFormatter(option.value)
            : option.display,
        });
      });
    }
  };

  toggleFilters = () => {
    this.setState(prevState => {
      const willBeOpen = !prevState.filtersOpen;
      return {
        filtersOpen: willBeOpen,
      };
    });
  };

  ensureFiltersVisible = () => {
    setSearchPath();
    if (!history.location.pathname.includes('/search')) {
      getSearchState().resetSearchState();
    }
    this.setState({ filtersOpen: true });
    if (isSmallSearchDevice(this.props.deviceType)) {
      ensureOnSearchPage('?showFilterOptions');
    }
  };

  getSelected = filter => {
    const selectedFilter =
      this.state.currentFilters && this.state.currentFilters[filter.dataLabel];
    if (selectedFilter) {
      return selectedFilter;
    }
  };

  render() {
    const disableApply = getSearchState().pristine;
    const selectedFiltersCount = getSearchState().appliedCount;
    const { displayError, deviceType } = this.props;
    if (displayError) {
      return null;
    }
    const target7InchTablet = isSevenInchTablet(deviceType);
    const smallSearchDevice = isSmallSearchDevice(deviceType);
    const { location } = this.props;
    if (location && location.pathname.match('/trophy-case')) {
      return null;
    }

    const disableClear = () => {
      if (selectedFiltersCount > 0) {
        return false;
      }
      return disableApply;
    };

    return (
      <>
        <div
          id="search-wrapper"
          className={classnames('search-wrapper', {
            'small-search-experience': smallSearchDevice,
          })}
        >
          <div id="search-canvas" className="search-canvas">
            <div
              className={classnames(`search-page ${this.props.themeClass}`, {
                'small-search-experience': smallSearchDevice,
              })}
            >
              {smallSearchDevice && (
                <div
                  className="fixed-mobile"
                  ref={ref => (this.mobileTextContain = ref)}
                >
                  <SearchField
                    target7InchTablet={target7InchTablet}
                    searchText={this.state.currentFilters?.query || ''}
                    onSubmit={this.applyFilters}
                    clearCount={this.state.filtersClearCount}
                    ref={ref => (this.searchField = ref)}
                    toggleFilters={this.toggleFilters}
                    filterCount={selectedFiltersCount}
                    filterOpen={this.state.filtersOpen}
                    filtersOpen={this.state.filtersOpen}
                    isMobile={isMobile(deviceType)}
                    sortStyle={{
                      top: smallSearchDevice
                        ? `${this.state.menuHeight}px`
                        : 'none',
                    }}
                    sortContainStyle={{
                      height: smallSearchDevice
                        ? `calc(100vh - ${this.state.menuHeight}px)`
                        : 'auto',
                    }}
                    onFocus={this.ensureFiltersVisible}
                    isSortDropdownOpen={this.isSortDropdownOpen}
                  />
                </div>
              )}
              <div
                id="search-menu"
                style={{
                  top: smallSearchDevice
                    ? `${this.state.menuHeight}px`
                    : 'none',
                }}
                className={classnames('search-menu', {
                  'mobile-menu': this.state.filtersOpen,
                })}
                ref={ref => (this.searchMenu = ref)}
              >
                <div
                  className="contain-filter"
                  style={{
                    height: smallSearchDevice
                      ? `calc(100vh - ${this.state.menuHeight}px)`
                      : 'auto',
                  }}
                >
                  {!smallSearchDevice && (
                    <SearchField
                      searchText={this.state.currentFilters?.query || ''}
                      onSubmit={this.applyFilters}
                      clearCount={this.state.filtersClearCount}
                      ref={ref => (this.searchField = ref)}
                      toggleFilters={this.toggleFilters}
                      filterCount={selectedFiltersCount}
                      filterOpen={this.state.filtersOpen}
                      isMobile={isMobile(deviceType)}
                      onFocus={this.ensureFiltersVisible}
                      isSortDropdownOpen={this.isSortDropdownOpen}
                    />
                  )}
                  {this.state.filtersOpen && (
                    <div
                      className="filter-bar"
                      ref={ref => (this.filterBar = ref)}
                    >
                      {this.state.filterData.map(filter => (
                        <FilterDropdown
                          isVault={this.props.isVault}
                          disableApply={disableApply}
                          filtersLoaded={Boolean(this.props.filterOptions)}
                          {...filter}
                          key={`dropdown-${filter.dataLabel}`}
                          onDropdownSelect={this.onDropdownSelect}
                          onFilterCheck={this.onFilterCheck}
                          selected={this.getSelected(filter)}
                          filtersOpen={this.state.filtersOpen}
                          isMobile={smallSearchDevice}
                        />
                      ))}
                      {!isMobile(deviceType) && (
                        <div
                          className={classnames('apply-clear-btns', {
                            'slide-up': this.state.filtersOpen,
                          })}
                        >
                          <button
                            className="apply"
                            onClick={this.applyFilters}
                            disabled={disableApply}
                          >
                            <span className="apply-text">
                              {applyButtonText()}
                            </span>
                          </button>
                          <button
                            disabled={disableClear()}
                            className="clear"
                            onClick={this.onClearFilters}
                          >
                            <span className="clear-text">
                              {clearButtonText()}
                            </span>
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {smallSearchDevice && (
                  <div
                    className={classnames('apply-clear-btns', {
                      'slide-up': this.state.filtersOpen,
                    })}
                  >
                    <button className="apply" onClick={this.applyFilters}>
                      <span className="apply-text">{applyButtonText()}</span>
                    </button>
                    <button className="clear" onClick={this.onClearFilters}>
                      <span className="clear-text">{clearButtonText()}</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {!smallSearchDevice && this.state.filtersOpen && (
          <div
            className="filtersopen-page-overlay"
            onClick={this.toggleFilters}
          ></div>
        )}
      </>
    );
  }
}

SearchHeader.propTypes = {
  deviceType: PropTypes.string,
  displayError: PropTypes.bool,
  themeClass: PropTypes.string,
  filterOptions: PropTypes.shape({
    trainers: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        id: PropTypes.string,
        image: PropTypes.string,
      }),
    ),
    categories: PropTypes.arrayOf(PropTypes.string),
    locations: PropTypes.arrayOf(PropTypes.string),
    continents: PropTypes.arrayOf(PropTypes.string),
    targetAreas: PropTypes.arrayOf(PropTypes.string),
    requiredEquipment: PropTypes.arrayOf(PropTypes.string),
    equipmentType: PropTypes.string,
  }),
  language: PropTypes.string,
};

const SearchHeaderThemed = withTheme(SearchHeader);
const SearchHooked = withHooks({
  filterOptions: function useFilterOptions() {
    const { data } = useGetFilterOptions();
    return data;
  },
  deviceType: function useGetDeviceType() {
    return useDeviceType();
  },
  isVault: function useGetConfig() {
    const { isVault } = useConfig();
    return isVault;
  },
})(SearchHeaderThemed);
const SearchRouted = withRouter(SearchHooked);
export default SearchRouted;
